import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import * as QueryString from 'query-string';
import PropTypes from 'prop-types';
import {
  login,
  loginWithToken,
  loadUser,
  logout
} from 'src/actions/accountActions';
import authService from 'src/services/authService';
import { getRepository } from 'src/actions/repositoryActions';
import { initializeAxios } from 'src/services/axiosService';
import SplashScreen from './SplashScreen';

function Auth({ children }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [isLoading, setLoading] = useState(true);
  const params = QueryString.parse(location.search, { arrayFormat: 'bracket' });

  const externalLogin = useCallback(async () => {
    try {
      await dispatch(params.$t
        ? loginWithToken(params.$t)
        : login(params.$u, params.$p));
    } catch (error) {
      await dispatch(logout());
    }
  }, [params.$u, params.$p, params.$t, dispatch]);

  useEffect(() => {
    const initAuth = async () => {
      initializeAxios({
        onLogout: async (error) => {
          if (!error) {
            await dispatch(logout());
          } else {
            authService.setSession(null);
          }
          history.push('/login');
        },
        dispatchFunc: dispatch
      });

      // if called from third party application
      if (params.$u || params.$t) {
        if (!authService.isAuthenticated()) {
          await externalLogin();
        }

        // if params include repository make sure to set it
        if (params.$r) {
          await dispatch(getRepository(params.$r));
        }

        // strip non-query parameters and set destination route
        const {
          $d, $u, $p, $t, ...queryObj
        } = params;

        history.push({
          pathname: $d === 'table' ? '/app/documents/list' : '/app/documents/dashboard',
          search: QueryString.stringify(queryObj)
        });
      }

      if (authService.isAuthenticated()) {
        try {
          await dispatch(loadUser());
        } catch (error) {
          setLoading(false);
        }
      }

      setLoading(false);
    };

    if (isLoading) {
      initAuth();
    }
  }, [params, history, dispatch, externalLogin]);

  return isLoading ? <SplashScreen /> : children;
}

Auth.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default Auth;
